/**
 * Export Jurisdiction and Classification U.S. EAR99, India Not Subject to Regulations
 */

export type Language = 'en-us' | 'fr';
const translation_version = import.meta.env.VITE_TRANSLATION_VERSION;
// Detect environment variable based on CRA or Vite
const getTranslationSource = (): 'file' | 'api' => {
  if (typeof import.meta !== 'undefined' && import.meta.env && import.meta.env.VITE_TRANSLATION_SOURCE) {
    return (import.meta.env.VITE_TRANSLATION_SOURCE as 'file' | 'api') || 'file';
  }

  return 'file'; 
};

const removeItemsWithPrefix = (prefix: string): void => {
  Object.keys(localStorage).forEach((key) => {
    if (key.startsWith(prefix)) {
      localStorage.removeItem(key);
    }
  });
};

export async function loadTranslations(lang: Language): Promise<Record<string, string>> {
  const localStorageKey = `translations-${lang}-${translation_version}`;
  const cachedLanguage = localStorage.getItem(localStorageKey);
  
  // Check if we have cached translations
  if (cachedLanguage) {
    try {
      return JSON.parse(cachedLanguage);
    } catch (e) {
      console.warn(`Invalid translation cache for ${lang}. Falling back to the local file.`);
    }
  } else {
    console.warn(`No translation cache for ${lang}. Falling back to the local file.`);
    removeItemsWithPrefix(`translations-${lang}`);
  }

  // Try loading translations from the API
  const source = getTranslationSource();
  if (source === 'api') {
    try {
      const response = await fetch(`https://dev.ciam.prattwhitney.com/openidm/config/uilocale/${lang}?_fields=enduser,shared`);
      if (response.ok) {
        const data = await response.json();
        // Cache the API data in localStorage
        localStorage.setItem(localStorageKey, JSON.stringify(data));
        return data;
      } else {
        console.warn(`Failed to load translations from API for ${lang}. Falling back to local file.`);
      }
    } catch (e) {
      console.warn(`API request failed for ${lang}. Falling back to local file.`, e);
    }
  }

  // Fallback: Load translations from local JSON file
  try {
    const url = `${import.meta.env.BASE_URL}assets/translations/${lang}.json`;
    const response = await fetch(url, { cache: 'no-store' }); // optional

    if (!response.ok) {
      throw new Error(`Translation file for ${lang} not found.`);
    }
    // Convert response to JSON
    const data: Record<string, string> = await response.json();
    localStorage.setItem(localStorageKey, JSON.stringify(data));
    return data;
  } catch (err) {
    console.error(`Failed to load local translation file for ${lang}`, err);
    return {};
  }
}
